import { Link } from "@tanstack/react-router";
import { CircleSlash2, X } from "lucide-react";
import React, { useState } from "react";
import { BanquetSeatingIcon } from "~/icons/BanquetSeatingIcon";
import { BlockSeatingIcon } from "~/icons/BlockSeatingIcon";
import { ClassroomSeatingIcon } from "~/icons/ClassroomSeatingIcon";
import { PartyIcon } from "~/icons/PartyIcon";
import { StandingSeatingIcon } from "~/icons/StandingSeatingIcon";
import { TableSeatingIcon } from "~/icons/TableSeatingIcon";
import { TheatreSeatingIcon } from "~/icons/TheatreSeatingIcon";
import { UFormSeatingIcon } from "~/icons/UFormSeatingIcon";
import { XIcon } from "~/icons/XIcon";
import {
  groupImagesByCategory,
  RoomWithImages,
} from "~/lib/server-functions/get-locations";
import noImageFound from "~/assets/no-image.svg";
import {
  IMAGE_SIZES,
  buildImageUrl,
  formatGermanNumberWithoutDecimals,
  convertCmToMeters,
  resolveAssetUrl,
} from "~/lib/utils";
import { Button } from "~/components/ui/button";
import ImageGalleryPopUp from "~/components/PopUp/ImageGalleryPopUp";
import RoomPopUp from "~/components/PopUp/RoomPopUp";

export type RoomLayouts = {
  party: string | number | null;
  row: string | number | null;
  parliament: string | number | null;
  block: string | number | null;
  uform: string | number | null;
  banquet: string | number | null;
  table: string | number | null;
  standing: string | number | null;
};

type RoomLayoutKey = keyof RoomLayouts;
const layoutsConfig: {
  icon: React.ReactNode;
  valueKey: RoomLayoutKey;
  label: string;
}[] = [
    { icon: <PartyIcon />, valueKey: "party", label: "Party" },
    { icon: <TheatreSeatingIcon />, valueKey: "row", label: "Reihe" },
    {
      icon: <ClassroomSeatingIcon />,
      valueKey: "parliament",
      label: "Parlament",
    },
    { icon: <BlockSeatingIcon />, valueKey: "block", label: "Block" },
    {
      icon: <UFormSeatingIcon />,

      valueKey: "uform",
      label: "U-Form",
    },
    {
      icon: <BanquetSeatingIcon />,
      valueKey: "banquet",
      label: "Bankett",
    },
    { icon: <TableSeatingIcon />, valueKey: "table", label: "Tafel" },
    { icon: <StandingSeatingIcon />, valueKey: "standing", label: "Stehempfang" },
  ];

const sections = [{ id: "images", label: "Bilder" }];

interface RoomCardProps {
  room: RoomWithImages;
}

export function RoomCard({ room }: RoomCardProps) {
  const layouts: RoomLayouts = {
    party: room.seatingParty || room.capacityPartySeating || null,
    row: room.seatingRow || room.capacityRowSeating || null,
    parliament:
      room.seatingParliamentary || room.capacityClassroomSeating || null,
    block: room.seatingBlock || room.capacityBlockSeating || null,
    uform: room.seatingUShape || room.capacityUSeating || null,
    banquet: room.seatingBanquet || room.capacityBanquetSeating || null,
    table: room.seatingTable || room.capacityTableSeating || null,
    standing: room.seatingStanding || null,
  };

  const [showDetails, setShowDetails] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState(sections[0].id);
  const [activeImageId, setActiveImageId] = useState<undefined | string>();
  const [showGallery, setShowGallery] = useState(false);

  const groupedRoomImages = groupImagesByCategory(room?.images ?? []);

  const flatGroupImages = Object.values(groupedRoomImages).flatMap((g) => {
    return g.images.map((img) => ({
      ...img,
      path: img.path as string,
      categoryId: g.categoryId,
      category: g.name,
    }));
  });

  return (
    <div>
      <RoomPopUp
        open={showDetails}
        images={flatGroupImages}
        room={room}
        onOpenChange={(value) => {
          setShowDetails(value);
        }}
        onOpenGallery={(value) => {
          setShowGallery(value);
        }}
      />
      <div
        key={room.id}
        className="flex flex-col gap-4 py-6 last:pb-0 md:flex-row md:items-center"
      >
        <div className="flex gap-4 shrink-0 md:mr-[100px] md:basis-[260px] shrink-0">
          {/* Image */}

          <img
            src={
              room.images[0]?.path
                ? buildImageUrl(room.images[0]!.path!, {
                    size: IMAGE_SIZES.cardThumb,
                  })
                : noImageFound
            }
            alt={room.name}
            className="h-[107px] w-[115px] shrink-0 rounded-lg object-cover"
          />

          {/* Infos */}
          <div className="flex flex-col">
            <div className="font-semibold">{room.name}</div>
            <div className="text-sm">
              {room.roomSize
                ? formatGermanNumberWithoutDecimals(convertCmToMeters(room.roomSize))
                : room.maxArea
                  ? formatGermanNumberWithoutDecimals(convertCmToMeters(room.maxArea))
                  : 0}{" "}
              m²
            </div>
            <Button
              variant="link"
              className="mt-1 p-0 inline-block text-sm font-medium text-dove-gray hover:underline"
              onClick={() => setShowDetails(!showDetails)}
            >
              Mehr Infos
            </Button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-start justify-start gap-4 md:mt-0 md:flex-nowrap">
          {/* Layouts */}
          {Object.entries(layouts).map(([key, value]) => {
            const item = layoutsConfig.find((l) => l.valueKey === key);
            if (!item) return null;

            return (
              <div
                key={key}
                className="flex flex-col items-center justify-center"
              >
                <div className="mb-2">{item.label}</div>
                <div className="rounded-md">{item.icon}</div>
                <span className="mt-2">{value ?? <XIcon />}</span>
              </div>
            );
          })}

          {/* Daylight */}
          {/* <div className="flex flex-col items-center justify-center text-xs text-gray-800">
                <div className="rounded-md">
                  <DaylightIcon />
                </div>
                <span className="mt-2">
                  {room.hasDaylight ? <CheckIcon /> : <XIcon />}
                </span>
              </div> */}

          {/* Parking */}
          {/* <div className="flex flex-col items-center justify-center text-xs text-gray-800">
                <div className="rounded-md">
                  <CarIcon />
                </div>
                <span className="mt-2">
                  {room.hasParking ? <CheckIcon /> : <XIcon />}
                </span>
              </div> */}

          {/* Price */}
          {/* <div className="flex flex-col items-center justify-center text-xs text-gray-800">
                <div className="rounded-md">
                  <EuroIcon />
                </div>
                <span className="mt-2">
                  {room.price ? room.price : "auf Anfrage"}
                </span>
              </div> */}
        </div>
      </div>
      {showDetails && (
        <div className="mt-4 border-t">
          <div className="w-full bg-white z-40 border-b-2 layout-place-full-width layout-content-grid mb-4 flex justify-center relative">
            <div className="flex gap-5 py-4 relative w-fit">
              {sections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setActiveDetailTab(s.id)}
                  className="relative group cursor-pointer px-2"
                >
                  {s.label}
                  {activeDetailTab === s.id ? (
                    <span className="absolute -bottom-[19px] left-0 w-full h-1 bg-dove-gray rounded-full"></span>
                  ) : (
                    <span className="absolute -bottom-[19px] left-0 w-full h-1 bg-dove-gray rounded-full scale-x-0 group-hover:scale-x-100"></span>
                  )}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowDetails(false)}
              className="absolute right-8 top-1/2 -translate-y-1/2 text-dove-gray hover:text-[#060606] hover:cursor-pointer"
            >
              <X />
            </button>
          </div>
          {activeDetailTab === "images" && (
            <>
              {room.images && room.images.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {room.images.map((img, index) => {
                    const isFifthOrSixth = index % 6 === 4 || index % 6 === 5;
                    return (
                      <button
                        key={index}
                        onClick={() => {
                          setActiveImageId(
                            img.id ? img.id.toString() : undefined
                          );
                          setShowGallery(true);
                        }}
                        className={`relative w-full h-[170px] max-h-[170px] overflow-hidden rounded-sm cursor-pointer ${isFifthOrSixth ? "col-span-2" : ""
                          }`}
                      >
                        <img
                          src={
                            img.path ? resolveAssetUrl(img.path) : noImageFound
                          }
                          alt={img.alt || ""}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}
      {showGallery && (
        <ImageGalleryPopUp
          open={showGallery}
          onOpenChange={(val) => setShowGallery(val)}
          images={flatGroupImages}
          activeImageId={activeImageId}
          sections={[
            { id: -1, label: "Alle Bilder" },
            ...Object.values(groupedRoomImages)
              .filter((g) => g.categoryId !== null && g.categoryId !== -1)
              .map((g) => ({
                id: g.categoryId!,
                label: g.name!,
              })),
          ]}
        />
      )}
    </div>
  );
}
